"use client";

import { usePathname } from "next/navigation";
import type { ReactNode } from "react";
import { AdProvider, AdSlot } from "@/components/ad-slot";
import { BibStringsProvider } from "@/components/citation-tool/tool-strings";
import { CommandPalette } from "@/components/command-palette";
import { CookieConsent } from "@/components/cookie-consent";
import { LanguageOffer } from "@/components/i18n/language-offer";
import { LocaleProvider } from "@/components/locale-link";
import { ReadingProgress } from "@/components/reading-progress";
import { ScrollToTop } from "@/components/scroll-to-top";
import { SiteFooter, SiteHeader } from "@/components/site-chrome";
import { Toaster } from "@/components/ui/sonner";
import { useBibliographyState } from "@/components/use-bibliography-state";
import { isEmbedWidgetPath } from "@/lib/embed-path";
import { unprefixedPath } from "@/lib/i18n";
import { DEFAULT_LOCALE } from "@/lib/i18n/locales";
import type { SiteChromeDictionary } from "@/lib/i18n/site-chrome-dictionary";
import { isBibliographyStudioPath } from "@/lib/studio-path";
import { cn } from "@/lib/utils";

/**
 * Chrome and page-level behaviour, for every language.
 *
 * This replaces the pair of wrappers that used to exist — one for the English tree,
 * one for the localized tree. They were 90% identical and drifted anyway, which is how
 * the localized footer ended up missing its legal links and its copyright line. One
 * component means anything added to the chrome appears in all 17 languages or in none.
 *
 * One structural detail still varies by language:
 *
 *  - **English pages render their own `<main>`.** The ~13,000 hand-written English
 *    pages each open one; the shared localized implementations render plain divs and
 *    rely on this wrapper for it. Wrapping the English ones again would nest two
 *    `<main>` elements, which is invalid and confuses screen readers. This collapses
 *    on its own as page types move to the shared content-driven implementations.
 */
/**
 * The sticky tower in the margin beside the page column.
 *
 * It only exists where that margin is wide enough to hold it with room to spare: the
 * column is 72rem, the tower 160px, and the breakpoint of 1600px leaves a clear gap on
 * either side of both. Below that width the rails are `display: none` and load
 * nothing. Each rail spans the full height of the content box it is placed in, so
 * `sticky` keeps the tower in view while the reader scrolls and releases it at the
 * bottom, before the footer strip and the footer.
 *
 * The offset from the top of the viewport matches the sticky header plus the same
 * gap the guide contents list uses. The inset passed to the slot is that offset plus
 * a bottom margin: the slot subtracts it from the viewport height to decide whether
 * the full tower fits or the short one is needed.
 */
function PageRail({ side }: { side: "left" | "right" }) {
  return (
    <div
      className={cn(
        "pointer-events-none absolute inset-y-0 z-10 hidden w-[calc((100%-72rem)/2)] min-[1600px]:block",
        side === "left" ? "left-0" : "right-0"
      )}
    >
      <div
        className={cn(
          "sticky top-24 flex px-4 pb-6",
          side === "left" ? "justify-end" : "justify-start"
        )}
      >
        <AdSlot
          className="pointer-events-auto w-[calc(160px+1.5rem+2px)]"
          placement="rail"
          viewportInset={120}
        />
      </div>
    </div>
  );
}

export function SiteLayoutWrapper({
  locale,
  dict,
  styleCount,
  children,
}: {
  locale: string;
  /** Only the chrome's slice of the dictionary — see `site-chrome-dictionary.ts`. */
  dict: SiteChromeDictionary;
  /** Preformatted style count — this is a client component and cannot read the catalog. */
  styleCount: string;
  children: ReactNode;
}) {
  const { citationCount } = useBibliographyState();

  // Which page the language selector should offer counterparts for. A layout cannot
  // see the route params of the page inside it, so it comes off the pathname — which
  // always carries a locale prefix now, English included.
  const pathname = usePathname() ?? `/${locale}`;
  const englishPath = unprefixedPath(pathname);

  if (isEmbedWidgetPath(pathname)) {
    return (
      <LocaleProvider locale={locale}>
        <BibStringsProvider value={dict.bib}>
          {children}
          <Toaster />
        </BibStringsProvider>
      </LocaleProvider>
    );
  }

  const isEnglish = locale === DEFAULT_LOCALE;
  const content = isEnglish ? (
    <div className="flex flex-1 flex-col" id="main-content">
      {children}
    </div>
  ) : (
    <main className="flex flex-1 flex-col" id="main-content">
      {children}
    </main>
  );

  const commandPalette = (
    <CommandPalette
      catalogTypeLabels={dict.catalogTypes}
      copy={{
        title: dict.nav.search,
        description: dict.searchPage.intro,
        inputLabel: dict.searchPage.inputLabel,
        inputPlaceholder: dict.searchPage.inputPlaceholder,
        loading: dict.searchPage.paletteLoading,
        error: dict.searchPage.paletteError,
        noMatches: dict.searchPage.noMatches,
        viewAll: dict.searchPage.paletteViewAll,
      }}
    />
  );
  const cookieConsent = (
    <CookieConsent
      strings={{
        notice: dict.common.cookieNotice,
        privacyPolicy: dict.common.privacyPolicy,
        accept: dict.common.accept,
        decline: dict.common.decline,
      }}
    />
  );

  // The Bibliography Studio is an application, not a page: it draws its own top bar
  // (with the search, language, and theme controls) and fills the viewport, so the
  // site header, footer, and reading-progress bar would only get in the way.
  if (isBibliographyStudioPath(pathname)) {
    return (
      <LocaleProvider locale={locale}>
        <BibStringsProvider value={dict.bib}>
          <a
            className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[70] focus:rounded-md focus:bg-primary focus:px-4 focus:py-2 focus:font-medium focus:text-primary-foreground focus:text-sm"
            href="#main-content"
          >
            {dict.common.skipToContent}
          </a>
          {content}
          {commandPalette}
          {cookieConsent}
          <LanguageOffer englishPath={englishPath} locale={locale} />
          <Toaster />
        </BibStringsProvider>
      </LocaleProvider>
    );
  }

  return (
    // The bibliography dialog and every save/note/in-text control below it read their
    // strings from here, so the whole tree gets them without prop-drilling. Ads are
    // switched on here and only here: the embed widget and the Studio above return
    // before this point, so they never carry one.
    <LocaleProvider locale={locale}>
      <BibStringsProvider value={dict.bib}>
        <AdProvider enabled={true} label={dict.common.advertisement}>
          <ReadingProgress />
          <a
            className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[70] focus:rounded-md focus:bg-primary focus:px-4 focus:py-2 focus:font-medium focus:text-primary-foreground focus:text-sm"
            href="#main-content"
          >
            {dict.common.skipToContent}
          </a>
          <SiteHeader
            citationCount={citationCount}
            dict={dict}
            englishPath={englishPath}
            locale={locale}
          />
          {/* The page column, with the ads that belong to every page: a strip at the
              top of the content, and on a wide desktop a tower pinned in the empty
              margin either side. The rails are absolutely bounded by this box, so
              they stay sticky through the content and stop where the footer starts. */}
          <div className="relative flex flex-1 flex-col">
            <PageRail side="left" />
            <PageRail side="right" />
            <div className="site-container pt-6 sm:pt-8">
              <AdSlot placement="leaderboard" />
            </div>
            {content}
          </div>
          {/* One strip above the footer on every page. */}
          <div className="site-container pb-10">
            <AdSlot placement="leaderboard" />
          </div>
          <SiteFooter
            dict={dict}
            englishPath={englishPath}
            locale={locale}
            styleCount={styleCount}
          />
          {commandPalette}
          <ScrollToTop />
          {cookieConsent}
          <LanguageOffer englishPath={englishPath} locale={locale} />
          <Toaster />
        </AdProvider>
      </BibStringsProvider>
    </LocaleProvider>
  );
}
